<template>
  <div class="toolbar">
    <div class="tabs">
      <div
        class="tab"
        :class="{ 'active': tab.value === toolbarState }"
        v-for="tab in currentTabs"
        :key="tab.value"
        @click="(!tab.disabled) && setToolbarState(tab.value)"
        :style="{ 'cursor': tab.disabled ? 'not-allowed' : 'pointer', 'opacity': tab.disabled ? '0.5' : '1' }"
      >{{tab.label}}</div>
    </div>
    <div class="content">
      <component :is="currentPanelComponent"></component>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, watch, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useMainStore, useWidgetStore } from '@/store'
import { ToolbarStates } from '@/types/toolbar'

import ElementStylePanel from './ElementStylePanel/index.vue'
import SectionPanel from './SectionPanel.vue'
import ElementPositionPanel from './ElementPositionPanel.vue'
import ElementAnimationPanel from './ElementAnimationPanel.vue'
import SlideDesignPanel from './SlideDesignPanel.vue'
import SlideAnimationPanel from './SlideAnimationPanel.vue'
import MultiPositionPanel from './MultiPositionPanel.vue'
import SymbolPanel from './SymbolPanel.vue'
import ElementDataOptionsPanel from './ElementDataOptionsPanel.vue'
import WidgetPositionPanel from "./WidgetPositionPanel.vue";


const {activeType} = storeToRefs(useWidgetStore())

watch(activeType, () => {
  if (activeType.value === 'none') {
    // only flip to toolbox tab if we're on the item tab
    if (toolbarState.value === 'widgetPosition') {
      mainStore.setToolbarState( ToolbarStates.EL_STYLE)
    }

  }
  else if (activeType.value === 'section') {
    mainStore.setToolbarState(ToolbarStates.SECTION)
  }
  else {
    // only flip to item tab if we're on the toolbox tab
    if (toolbarState.value === 'elStyle') {
      mainStore.setToolbarState(ToolbarStates.WIDGET_POSITION)
    }
  }
})

interface ElementTabs {
  label: string
  value: ToolbarStates
  disabled?: boolean
}

const mainStore = useMainStore()
const { handleElement, toolbarState, activeDropzone } = storeToRefs(mainStore)

onMounted(() => {
  toolbarState.value = 'elDataOptions'
  setTimeout(() => {
    toolbarState.value = 'elStyle'
  }, 50)
})

const elementTabs = computed<ElementTabs[]>(() => {
  if (handleElement.value?.type === 'shape' || handleElement.value?.type === 'line') {
    return [
      { label: 'Element', value: ToolbarStates.EL_STYLE }
    ]
  }

  return [
    { label: 'Content', value: ToolbarStates.EL_STYLE },
    { label: 'Tile Properties', value: ToolbarStates.WIDGET_POSITION, disabled: activeType.value === 'none' },
    { label: 'Designer', value: ToolbarStates.SECTION }
  ]
})

const slideTabs = [
  { label: 'Design', value: ToolbarStates.SLIDE_DESIGN },
  { label: 'Switch', value: ToolbarStates.SLIDE_ANIMATION },
  { label: 'Animation', value: ToolbarStates.EL_ANIMATION },
]
const multiSelectTabs = [
  { label: 'Style', value: ToolbarStates.EL_STYLE },
  { label: 'Position', value: ToolbarStates.MULTI_POSITION },
]

const setToolbarState = (value: ToolbarStates) => {
  mainStore.setToolbarState(value)
}

const currentTabs = computed(() => {
  return elementTabs.value
})

watch(currentTabs, () => {
  const currentTabsValue: ToolbarStates[] = currentTabs.value.map(tab => tab.value)

  if (!currentTabsValue.includes(toolbarState.value)) {
    mainStore.setToolbarState(currentTabsValue[0])
  }
})

const currentPanelComponent = computed(() => {
  const panelMap = {
    [ToolbarStates.EL_STYLE]: ElementStylePanel,
    [ToolbarStates.SECTION]: SectionPanel,
    [ToolbarStates.EL_POSITION]: ElementPositionPanel,
    [ToolbarStates.EL_ANIMATION]: ElementAnimationPanel,
    [ToolbarStates.SLIDE_DESIGN]: SlideDesignPanel,
    [ToolbarStates.SLIDE_ANIMATION]: SlideAnimationPanel,
    [ToolbarStates.MULTI_POSITION]: MultiPositionPanel,
    [ToolbarStates.SYMBOL]: SymbolPanel,
    [ToolbarStates.EL_DATA_OPTIONS]: ElementDataOptionsPanel,
    [ToolbarStates.WIDGET_POSITION]: WidgetPositionPanel,

  }
  return panelMap[toolbarState.value] || null
})
</script>

<style lang="scss" scoped>
.toolbar {
  border-left: solid 1px $borderColor;
  background-color: #fff;
  display: flex;
  flex-direction: column;
}
.tabs {
  height: 40px;
  font-size: 12px;
  flex-shrink: 0;
  display: flex;
  user-select: none;
}
.tab {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: $lightGray;
  border-bottom: 1px solid $borderColor;
  cursor: pointer;

  &.active {
    background-color: #fff;
    border-bottom: 1px solid $borderColor
  }

  & + .tab {
    border-left: 1px solid $borderColor;
  }
}
.content {
  padding: 5px 10px 5px 10px;
  font-size: 13px;
  height: calc(100% - 40px);
  @include overflow-overlay();

  :deep( .p-component ) {
    font-size: 13px;
  }

  :deep( .p-inputtext ) {
    font-size: 13px;
  }
}


</style>
